import { Component, OnInit, Inject, ViewChild } from '@angular/core';
import { FormGroup, FormArray, FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { TslDialog, TslFileDrop } from '@tesla/ng-tds';
import { GeneralSettings } from 'app/profile/models/generic.model';
import { DataWithSupplierCode, EmailDomainPropertyValue, OPERATION, PropertyValue, SupplierDocument, SupplierDocumentPropertyValues } from 'app/profile/models/supplier.model';
import { ContactsService } from 'app/profile/services/contacts.service';
import { FileUploadService } from '../../../../../common/services/fileUpload.service';
import { MasterDataService } from 'app/profile/services/masterdata.service';
import { SupplierStateService } from 'app/profile/services/supplierstate.service';
import { getGeneralSettings, SelectSupplierDocumentList } from 'app/profile/store/app.selector';
import { SupplierState } from 'app/profile/store/state/supplierstate.state';
import { transformEmailDomain, validateAllFormFields } from 'app/shared/utils/utils';
import { Observable } from 'rxjs';
import { I18nService } from 'app/shared/services/i18n.service';


@Component({
  selector: 'email-domain-form',
  templateUrl: './email-domain-form.component.html',
  styleUrls: ['./email-domain-form.component.scss']
})
export class EmailDomainFormComponent implements OnInit {
  @ViewChild('fileDrop', {static: false}) fileDrop: TslFileDrop;

  domainForm: FormGroup;
  reasons$: Observable<string[]>;
  docsList: SupplierDocumentPropertyValues[];
  settings: GeneralSettings;

  get DomainFormControls() {
    if (!this.domainForm) {
      return {};
    }

    return {
      domain: this.domainForm.get('domain'),
      reason: this.domainForm.get('reason'),
      website: this.domainForm.get('website'),
      comment: this.domainForm.get('comment'),
      documents: this.domainForm.get('documents') as FormArray
    };
  }

  constructor(
    private fb: FormBuilder,
    private dialog: TslDialog,
    private contactService: ContactsService,
    private masterDataService: MasterDataService,
    public dialogRef: MatDialogRef<EmailDomainFormComponent>,
    private store: Store<SupplierState>,
    private fileUploadService: FileUploadService,
    private translate: I18nService,
    @Inject(MAT_DIALOG_DATA) public matData: DataWithSupplierCode<EmailDomainPropertyValue>,
  ) {
    if (this.matData.data) {
      this.matData.data = { ...this.matData.data };
      this.matData.data.operation = new PropertyValue<OPERATION>();
      this.matData.data.operation.newValue = OPERATION.UPDATE;
    } else {
      this.matData.data = new EmailDomainPropertyValue();
      this.matData.data.operation = new PropertyValue<OPERATION>();
      this.matData.data.operation.newValue = OPERATION.CREATE;
    }

    this.store.select(getGeneralSettings).subscribe(res => this.settings = res);
    this.domainForm = this.fb.group({
      domain: [this.matData.data.domain.newValue, [Validators.required]],
      reason: [this.matData.data.reason.newValue, [Validators.required]],
      website: [this.matData.data.website.newValue],
      comment: [this.matData.data.comment.newValue],
      documents: this.fb.array([])
    });

    this.store.select(SelectSupplierDocumentList).subscribe(docsList => {
      if (docsList && docsList.length > 0) {
        this.docsList = new Array(...docsList);
        this.bindDocumentForm();
      }
    });
  }

  ngOnInit() {
    this.reasons$ = this.masterDataService.getEmailDomainReasons()
      .map(y => {
        if (y) {
          return y;
        }
      })
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  save() {
    if (this.domainForm.valid) {
      if (!this.DomainFormControls.domain.pristine) {
        this.matData.data.domain.newValue = this.DomainFormControls.domain.value;
      }
      if (!this.DomainFormControls.reason.pristine) {
        this.matData.data.reason.newValue = this.DomainFormControls.reason.value;
      }
      if (!this.DomainFormControls.website.pristine) {
        this.matData.data.website.newValue = this.DomainFormControls.website.value;
      }
      if (!this.DomainFormControls.comment.pristine) {
        this.matData.data.comment.newValue = this.DomainFormControls.comment.value;
      }
      if (this.documents.length > 0) {
        let file = this.documents.controls.filter(d => !d.get('documentId').value)[0].get('attachment').value as File;
        this.fileUploadService.uploadFileToServer([file], this.matData.suppliercode).subscribe(doc => {
          if (doc) {
            const document = new SupplierDocument();
            document.Operation = OPERATION.CREATE;
            document.DocumentID = doc.documentID;
            document.DocumentType = 'ED';
            document.FileName = doc.fileName;
            document.DocumentVersion = doc.documentVersion;
            document.ReferenceSystem = 'DMS';
            this.matData.data.documents.newValue = [document.DocumentID];
            this.processDomainChange(document);
          }
        });
      } else {
        this.processDomainChange(null);
      }
    } else {
      validateAllFormFields(this.domainForm);
    }
  }

  processDomainChange(document: SupplierDocument) {
    const result = transformEmailDomain(this.matData.data);
    this.contactService.processEmailDomainChange(this.matData.suppliercode, result, document)
      .subscribe(x => {
        if (x) {
          this.dialogRef.close(this.matData.data);
        }
      });
  }

  getDocumentGroup(x: SupplierDocumentPropertyValues): FormGroup {
    return this.fb.group({
      documentId: [x.documentID.newValue, null],
      fileName: [x.fileName.newValue, Validators.required],
      attachment: [null]
    });
  }

  getDocumentsGroup(docList: SupplierDocumentPropertyValues[]): FormGroup[] {
    return docList.map(x => {
      return this.getDocumentGroup(x);
    });
  }

  bindDocumentForm() {
    if (this.matData.data) {
      if (this.matData.data.documents.newValue && this.matData.data.documents.newValue.length > 0 && this.docsList && this.docsList.length > 0) {
        let docList = this.docsList.find(x => this.matData.data.documents.newValue && this.matData.data.documents.newValue.includes(x.documentID.newValue));
        (this.domainForm.get('documents') as FormArray).push(this.getDocumentGroup(docList));
      }
    }
  }

  get documents() {
    return this.DomainFormControls.documents;
  }

  get getAcceptedFormat() {
    return '*';
  }

  get fileSizeLimitMB() { return this.settings.fileSizeLimitMB * 1000000; }

  onFileAdd(event): void {
    let file = event[0];
    if (file) {
      if (file.size > this.fileSizeLimitMB) {
        this.dialog.alert('Alert', this.translate.get('File size can not be more than 10MB!'));
      }
      else {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        let documentGroup = this.getDocumentGroup(new SupplierDocumentPropertyValues());
        documentGroup.patchValue({ attachment: file, fileName: file.name });
        documentGroup.setValidators(Validators.required);
        documentGroup.updateValueAndValidity();
        if (!this.documents) {
          this.DomainFormControls.documents.setValue([documentGroup]);
        }
        else {
          this.documents.push(documentGroup);
        }
      }
    }
  }

  removeFile(index: number) {
    this.fileDrop.reset(true);
    if (this.documents) {
      this.documents.removeAt(index);
    }
  }
}
